import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useServices } from '@/contexts/app-services';
import { useRouteInfo } from '@/hooks/useRouteInfo';
import { useTargetStartAt } from '@/hooks/useTargetStartAt';
import { isToday } from '@/utils/datetime';
import { CheckInView } from '../components/CheckInView';
import { PlanView } from '../components/PlanView';
import { useOrCreatePlan } from '@/queries/plan';
import { useSeenStatus } from '../hooks/useSeenStatus';

interface PlanSceneProps {
  className?: string;
}

// The morning check-in is currently disabled for everyone, so opening a day
// goes straight to the plan. Flip this to true to re-enable it.
const CHECK_IN_ENABLED: boolean = false;

function getPlanView(
  period: string,
  startAtLocal: string | undefined,
  planStatus: string | undefined,
): 'check-in' | 'plan' {
  if (
    CHECK_IN_ENABLED &&
    period === 'day' &&
    !!startAtLocal &&
    isToday(startAtLocal) &&
    planStatus === 'created'
  ) {
    return 'check-in';
  }
  return 'plan';
}

export function PlanScene({ className }: PlanSceneProps) {
  const navigate = useNavigate();
  const { analytics } = useServices();
  const { periodParam: period, targetDateParam } = useRouteInfo();
  const targetStartAtResult = useTargetStartAt(period, targetDateParam);
  const startAtLocal = targetStartAtResult?.startAtLocal;
  const { markSeen } = useSeenStatus(period, startAtLocal ?? '');

  const { plan, isLoading } = useOrCreatePlan(
    period,
    targetStartAtResult?.startAt ?? new Date(),
    startAtLocal ?? '',
  );

  const view = getPlanView(period, startAtLocal, plan?.status);

  // Redirect if invalid date
  useEffect(() => {
    if (targetStartAtResult === undefined) {
      void navigate('/', { replace: true });
    }
  }, [targetStartAtResult, navigate]);

  useEffect(() => {
    if (!startAtLocal || !plan) {
      return;
    }
    analytics.trackViewedPlan({ period, startAtLocal, view });
    markSeen();
  }, [analytics, markSeen, period, startAtLocal, plan, view]);

  // Don't render if invalid date (will redirect)
  if (targetStartAtResult === undefined) {
    return null;
  }

  const { startAt: targetStartAt, startAtLocal: targetStartAtLocal } =
    targetStartAtResult;

  if (period === 'day' && isToday(targetStartAtLocal) && isLoading) {
    return null;
  }

  if (plan && view === 'check-in') {
    return (
      <CheckInView
        plan={plan}
        period={period}
        targetStartAt={targetStartAt}
        targetStartAtLocal={targetStartAtLocal}
      />
    );
  }

  return (
    <PlanView
      className={className}
      period={period}
      targetStartAt={targetStartAt}
      targetStartAtLocal={targetStartAtLocal}
    />
  );
}
